import type { Ref } from 'vue'
import { ref, watch, nextTick } from 'vue'

// ─── Types ────────────────────────────────────────────────────────────────────

export type SchemaFieldType =
    | 'string'
    | 'number'
    | 'integer'
    | 'boolean'
    | 'object'
    | 'array/string'
    | 'array/number'
    | 'array/boolean'
    | 'array/integer'
    | 'array/object'

export interface SchemaField {
    id: string
    key: string
    type: SchemaFieldType
    description: string
    constValue?: string
    enumValues?: string[]
    required: boolean
    children: SchemaField[]
}

export type JsonSchema = Record<string, unknown>

// ─── Constants ────────────────────────────────────────────────────────────────

const NESTED_TYPES = new Set<SchemaFieldType>(['object', 'array/object'])

const PRIMITIVE_TYPE_MAP: Record<string, SchemaFieldType> = {
    string: 'string',
    number: 'number',
    integer: 'integer',
    boolean: 'boolean',
}

const ARRAY_ITEM_TYPE_MAP: Record<string, SchemaFieldType> = {
    string: 'array/string',
    number: 'array/number',
    boolean: 'array/boolean',
    integer: 'array/integer',
    object: 'array/object',
}

export const FIELD_TYPE_OPTIONS: SchemaFieldType[] = [
    'string',
    'number',
    'boolean',
    'integer',
    'object',
    'array/string',
    'array/number',
    'array/boolean',
    'array/integer',
    'array/object',
]

// ─── ID generator ─────────────────────────────────────────────────────────────

let _counter = 0
const uid = () => `sf_${++_counter}`

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function isNestedType(type: SchemaFieldType): boolean {
    return NESTED_TYPES.has(type)
}

export function createField(): SchemaField {
    return {
        id: uid(),
        key: '',
        type: 'string',
        description: '',
        constValue: undefined,
        enumValues: undefined,
        required: true,
        children: [],
    }
}

// ─── Conversion: JSON Schema → SchemaField[] ─────────────────────────────────

function resolveFieldType(typeDef: unknown, items?: unknown): SchemaFieldType {
    if (typeDef === 'array') {
        const itemType = (items as { type?: string } | undefined)?.type
        return ARRAY_ITEM_TYPE_MAP[itemType ?? ''] ?? 'array/string'
    }
    if (typeDef === 'object') {
        return 'object'
    }
    return PRIMITIVE_TYPE_MAP[typeDef as string] ?? 'string'
}

function parseProperties(
    properties: Record<string, unknown>,
    requiredKeys?: string[],
): SchemaField[] {
    const requiredSet = new Set(requiredKeys ?? [])
    return Object.entries(properties).map(([key, def]) => {
        const d = def as Record<string, unknown>
        const type = resolveFieldType(d.type, d.items)

        let children: SchemaField[] = []
        if (type === 'object') {
            children = parseProperties(
                (d.properties as Record<string, unknown>) ?? {},
                d.required as string[] | undefined,
            )
        } else if (type === 'array/object') {
            const items = d.items as Record<string, unknown> | undefined
            children = parseProperties(
                (items?.properties as Record<string, unknown>) ?? {},
                items?.required as string[] | undefined,
            )
        }

        const constValue = d.const !== undefined ? String(d.const) : undefined

        const enumSource = type.startsWith('array/')
            ? (d.items as Record<string, unknown> | undefined)
            : d
        const rawEnum = enumSource?.enum
        const enumValues = Array.isArray(rawEnum)
            ? (rawEnum as unknown[]).map((v) => String(v))
            : undefined

        return {
            id: uid(),
            key,
            type,
            description: (d.description as string) ?? '',
            constValue,
            enumValues,
            required: requiredKeys !== undefined ? requiredSet.has(key) : false,
            children,
        }
    })
}

export function detectRootType(schema: JsonSchema): 'object' | 'array' {
    return schema.type === 'array' ? 'array' : 'object'
}

export function schemaToFields(schema: JsonSchema): SchemaField[] {
    if (schema.type === 'array') {
        const items = schema.items as JsonSchema | undefined
        const properties = items?.properties as
            | Record<string, unknown>
            | undefined
        if (!properties) {
            return []
        }
        return parseProperties(
            properties,
            items?.required as string[] | undefined,
        )
    }
    const properties = schema.properties as Record<string, unknown> | undefined
    if (!properties) {
        return []
    }
    return parseProperties(properties, schema.required as string[] | undefined)
}

// ─── Conversion: SchemaField[] → JSON Schema ─────────────────────────────────

function coerceEnumValue(value: string, type: SchemaFieldType): unknown {
    const base = type.replace('array/', '')
    if (base === 'number' || base === 'integer') {
        const n = Number(value)
        return Number.isNaN(n) ? value : n
    }
    if (base === 'boolean') {
        return value === 'true'
    }
    return value
}

function buildProperty(field: SchemaField): Record<string, unknown> {
    const base: Record<string, unknown> = {}
    if (field.description) {
        base.description = field.description
    }

    if (field.constValue !== undefined && field.constValue !== '') {
        return { type: field.type, const: field.constValue, ...base }
    }

    const enumValues = field.enumValues?.filter((v) => v !== '')
    const enumArray =
        enumValues && enumValues.length > 0
            ? enumValues.map((v) => coerceEnumValue(v, field.type))
            : undefined

    if (field.type === 'object') {
        return { ...fieldsToSchema(field.children), ...base }
    }
    if (field.type === 'array/object') {
        return {
            type: 'array',
            items: fieldsToSchema(field.children),
            ...base,
        }
    }
    if (field.type.startsWith('array/')) {
        const items: Record<string, unknown> = {
            type: field.type.replace('array/', ''),
        }
        if (enumArray) {
            items.enum = enumArray
        }
        return {
            type: 'array',
            items,
            ...base,
        }
    }
    if (enumArray) {
        return { type: field.type, enum: enumArray, ...base }
    }
    return { type: field.type, ...base }
}

export function fieldsToSchema(
    fields: SchemaField[],
    rootType: 'object' | 'array' = 'object',
): JsonSchema {
    const validFields = fields.filter((f) => f.key)
    const properties: Record<string, unknown> = {}
    for (const field of validFields) {
        properties[field.key] = buildProperty(field)
    }
    const requiredKeys = validFields.filter((f) => f.required).map((f) => f.key)
    const objectSchema: JsonSchema = { type: 'object', properties }
    if (requiredKeys.length > 0) {
        objectSchema.required = requiredKeys
    }
    if (rootType === 'array') {
        return { type: 'array', items: objectSchema }
    }
    return objectSchema
}

// ─── Composable ───────────────────────────────────────────────────────────────

export function useJsonSchemaEditor(
    modelValue: Ref<JsonSchema | null | undefined>,
) {
    const rootType = ref<'object' | 'array'>(
        modelValue.value ? detectRootType(modelValue.value) : 'object',
    )
    const fields = ref<SchemaField[]>(
        modelValue.value ? schemaToFields(modelValue.value) : [],
    )
    const codeValue = ref(
        modelValue.value ? JSON.stringify(modelValue.value, null, 2) : '',
    )

    let syncing = false

    /** Applies a schema value to all three representations without triggering cyclic watchers. */
    function syncAll(schema: JsonSchema | null) {
        syncing = true
        modelValue.value = schema
        rootType.value = schema ? detectRootType(schema) : 'object'
        fields.value = schema ? schemaToFields(schema) : []
        codeValue.value = schema ? JSON.stringify(schema, null, 2) : ''
        nextTick(() => {
            syncing = false
        })
    }

    // Visual fields → modelValue
    watch(
        fields,
        (newFields) => {
            if (syncing) {
                return
            }
            syncing = true
            modelValue.value = fieldsToSchema(newFields, rootType.value)
            codeValue.value = modelValue.value
                ? JSON.stringify(modelValue.value, null, 2)
                : ''
            nextTick(() => {
                syncing = false
            })
        },
        { deep: true },
    )

    // External modelValue change → sync fields + code
    watch(
        () => modelValue.value,
        (newVal) => {
            if (syncing) {
                return
            }
            syncAll(newVal ?? null)
        },
        { deep: true },
    )

    // ─── Code editor handlers ─────────────────────────────────────────────────

    function updateFromCode(val: string | undefined) {
        codeValue.value = val ?? ''
        try {
            const parsed = val ? (JSON.parse(val) as JsonSchema) : null
            syncing = true
            rootType.value = parsed ? detectRootType(parsed) : 'object'
            fields.value = parsed ? schemaToFields(parsed) : []
            modelValue.value = parsed
            nextTick(() => {
                syncing = false
            })
        } catch {
            // Keep existing model while user is typing invalid JSON
        }
    }

    // ─── Root type toggle ─────────────────────────────────────────────────────

    function setRootType(newType: 'object' | 'array') {
        if (newType === rootType.value) {
            return
        }
        rootType.value = newType
        const newSchema =
            fields.value.length > 0
                ? fieldsToSchema(fields.value, newType)
                : null
        syncing = true
        modelValue.value = newSchema
        codeValue.value = newSchema ? JSON.stringify(newSchema, null, 2) : ''
        nextTick(() => {
            syncing = false
        })
    }

    // ─── Field CRUD ───────────────────────────────────────────────────────────

    function addField() {
        fields.value = [...fields.value, createField()]
    }

    function updateField(index: number, updated: SchemaField) {
        fields.value = fields.value.map((f, i) => (i === index ? updated : f))
    }

    function removeField(index: number) {
        fields.value = fields.value.filter((_, i) => i !== index)
    }

    return {
        fields,
        codeValue,
        rootType,
        syncAll,
        updateFromCode,
        setRootType,
        addField,
        updateField,
        removeField,
    }
}
